import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth, can } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/api";
import {
  LayoutDashboard, Package, ArrowDownToLine, ArrowUpFromLine,
  History, Tag, Users, ScanBarcode, LogOut, Menu, X, FileUp, Smartphone, BarChart3, Truck,
  ClipboardList, Building2, Wrench, Calendar, PackageMinus
} from "lucide-react";

const LOGO = "/assets/germac-logo.png";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/schede", icon: ClipboardList, label: "Schede Riparazione" },
  { to: "/calendario", icon: Calendar, label: "Calendario" },
  { to: "/clienti", icon: Building2, label: "Clienti" },
  { to: "/macchine", icon: Wrench, label: "Macchine" },
  { to: "/smontaggi", icon: PackageMinus, label: "Smontaggi" },
  { to: "/prodotti", icon: Package, label: "Prodotti" },
  { to: "/carico", icon: ArrowDownToLine, label: "Carico", needs: "carico" },
  { to: "/carico-pdf", icon: FileUp, label: "Carico PDF", needs: "carico" },
  { to: "/scarico", icon: ArrowUpFromLine, label: "Scarico", needs: "scarico" },
  { to: "/scarico-mobile", icon: Smartphone, label: "Scarico Mobile", needs: "scarico" },
  { to: "/movimenti", icon: History, label: "Movimenti" },
  { to: "/reports", icon: BarChart3, label: "Reports" },
  { to: "/officine-mobili", icon: Truck, label: "Officine Mobili" },
  { to: "/etichette", icon: Tag, label: "Etichette" },
  { to: "/scanner", icon: ScanBarcode, label: "Scanner" },
  { to: "/utenti", icon: Users, label: "Utenti", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const visible = NAV.filter(n => {
    if (n.adminOnly && user.role !== "admin") return false;
    if (n.needs && !can(user, n.needs)) return false;
    return true;
  });

  const onLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-[#050505]">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-zinc-800 bg-[#0a0d12]">
        <div className="h-20 flex items-center gap-3 px-5 border-b border-zinc-800">
          <div className="h-12 w-12 bg-white p-1 flex items-center justify-center rounded-sm">
            <img src={LOGO} alt="GERMAC" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="font-display text-xl font-black uppercase leading-none">GERMAC</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mt-1">Magazzino</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {visible.map(n => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({isActive}) =>
                `flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-wider font-semibold transition-colors ${
                  isActive
                    ? "bg-[#4CAF50] text-white"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`
              }
            >
              <n.icon size={18} strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <div className="px-3 py-2">
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Connesso come</div>
            <div className="text-sm font-semibold text-white mt-1 truncate">{user.name}</div>
            <div className="text-xs text-[#4CAF50] uppercase tracking-wider mt-0.5">{ROLE_LABELS[user.role]}</div>
          </div>
          <button data-testid="logout-button" onClick={onLogout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 text-sm uppercase tracking-wider text-zinc-400 hover:text-[#FF3B30] hover:bg-zinc-900 transition-colors">
            <LogOut size={16} /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0a0d12] border-b border-zinc-800 flex items-center justify-between gm-mobile-topbar"
      >
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-white p-0.5 flex items-center justify-center rounded-sm">
            <img src={LOGO} alt="GERMAC" className="h-full w-full object-contain" />
          </div>
          <span className="font-display text-lg font-black uppercase">GERMAC</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={()=>setOpen(!open)} className="p-2 text-white">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-[#050505] overflow-y-auto gm-mobile-drawer"
        >
          <nav className="p-4 space-y-1">
            {visible.map(n => (
              <NavLink
                key={n.to} to={n.to} end={n.end}
                onClick={()=>setOpen(false)}
                className={({isActive}) =>
                  `flex items-center gap-3 px-4 py-4 text-base uppercase tracking-wider font-semibold ${
                    isActive ? "bg-[#4CAF50] text-white" : "text-zinc-300 bg-zinc-900/40"
                  }`
                }
              >
                <n.icon size={20} />
                {n.label}
              </NavLink>
            ))}
            <button data-testid="logout-button-mobile" onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-4 text-base uppercase tracking-wider font-semibold text-[#FF3B30] bg-zinc-900/40 mt-3">
              <LogOut size={20} /> Esci ({user.name})
            </button>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pb-20 lg:pb-0 gm-mobile-main">
        <Outlet />
      </main>

      {/* Sticky mobile scan button - for meccanico go to scarico-mobile */}
      <Link
        to={user.role === "meccanico" ? "/scarico-mobile" : "/scanner"}
        data-testid="mobile-scan-button"
        className="lg:hidden fixed right-4 h-16 w-16 bg-[#4CAF50] text-white flex items-center justify-center shadow-lg z-30 rounded-sm gm-mobile-fab"
      >
        <ScanBarcode size={28} strokeWidth={2.5} />
      </Link>
    </div>
  );
}
